import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState, useAppDispatch } from "../store";
import { showModal } from "../store/actions/modalActions";
import {
  LicensePlan,
  accessAiFeatures,
  accessSections,
  isInGrace,
  isLicensingEnforced,
  isReadOnly as accessIsReadOnly,
  isRenewalLocked,
  isTrialing,
} from "../store/actions/PlanActions";
import { RENEWAL_CATEGORY, renewalDescription } from "../components/shared/Licenses/renewal";
import { translate } from "../i18n/translate";
import {
  ROUTE_TO_SECTION,
  UPGRADE_PATH,
  aiFeatureLabel,
  sectionLabel,
} from "../config/entitlementKeys";

/* ─────────────────────────────────────────────────────────
   LICENSING ENFORCEMENT - decided by the backend, per org, via
   `licensing_enforced` on /licensing/my-access. See
   `isLicensingEnforced`: not loaded reads as "not enforced" (pilot
   behaviour - all access granted), and the server still refuses.
   ───────────────────────────────────────────────────────── */

/*
  THE KEYS COME FROM ONE PLACE - `config/entitlementKeys`.

  These three maps used to live here as literals, and they had drifted from
  the grants they are compared against: `/contacts` asked for `clients` and
  `/reviews` asked for `review`, both renamed across the whole catalogue by
  backend Story 1205. The comparison is a bare `includes()`, so a stale key
  does not error - it just answers "no", and an org on PONS Full found
  Contacts and Reviews locked behind an upgrade wall for the plan it was
  already paying for.
*/

/* ─────────────────────────────────────────────────────────
   Find which plan/addon unlocks a given section or feature
   ───────────────────────────────────────────────────────── */
function findUnlockingPlan(
  section: string | null,
  aiFeature: string | null,
  plans: LicensePlan[],
  addons: LicensePlan[]
): { plan: LicensePlan | null; isAddon: boolean } {
  // Check addons first (they're the targeted unlock for specific sections)
  for (const addon of addons) {
    if (
      (section && addon.feature_gates.sections.includes(section)) ||
      (aiFeature && addon.feature_gates.ai_features.includes(aiFeature))
    ) {
      return { plan: addon, isAddon: true };
    }
  }
  // Check base plans (higher tiers unlock more sections)
  for (const plan of plans) {
    if (
      (section && plan.feature_gates.sections.includes(section)) ||
      (aiFeature && plan.feature_gates.ai_features.includes(aiFeature))
    ) {
      return { plan, isAddon: false };
    }
  }
  return { plan: null, isAddon: false };
}

/** Stable empty fallback for the plan/addon selectors - see below. */
const NO_PLANS: LicensePlan[] = [];

/* ─────────────────────────────────────────────────────────
   Hook
   ───────────────────────────────────────────────────────── */
export function useLicenseGate() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const access = useSelector((state: RootState) => state.license?.access);
  // A shared frozen empty array, so the fallback branch returns the SAME
  // reference every time - a `?? []` inside a selector makes react-redux see a
  // changed result on every store notification and re-render the subscriber.
  const plans = useSelector((state: RootState) => state.license?.plans ?? NO_PLANS);
  const addons = useSelector((state: RootState) => state.license?.addons ?? NO_PLANS);
  const loaded = useSelector((state: RootState) => state.license?.loaded ?? false);
  const enforced = useSelector(isLicensingEnforced);
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user?.userInfo?.is_org_admin === true
  );

  /*
    THESE THREE MUST BE MEMOIZED.

    `?? []` / `?? {}` mints a fresh literal on every render whenever the
    license payload is absent or partial - which is the normal state while the
    license call is in flight, and the permanent state wherever licensing is
    not provisioned. That fresh identity flowed into the `useCallback` deps
    below, so `hasAiFeatureAccess` / `hasSectionAccess` were new functions on
    every render. Any effect depending on one of them re-ran every render; if
    it also set state (MatterDetails' data-sources effect did) the two fed
    each other and the fetch looped without bound.
  */
  /* `accessSections` / `accessAiFeatures` are THE readers - they take the
     canonical top-level list and fall back to the nested one, so no gate is
     keyed on a field name the normaliser might stop writing. */
  const licensedSections = useMemo(() => accessSections(access), [access]);
  const licensedAiFeatures = useMemo(() => accessAiFeatures(access), [access]);
  const hasBaseSeat = access?.has_base_seat ?? true;
  const planType = access?.license_info?.plan_type ?? null;
  const planName = access?.license_info?.plan_name ?? null;

  // Only PONS Starter supports add-ons
  const isAddonEligible = planName === "pons_starter";
  const isTrail = planType === "trial";
  const limits = useMemo(
    () => access?.effective_features?.limits ?? {},
    [access]
  );

  /*
    ONLY A BELIEVABLE PAYLOAD IS EVER LOADED.

    `fetchMyAccess` refuses to store an empty section list unless the backend
    says why (expired, never licensed, read-only, not enforced) - see
    `isUsableAccess`. So `loaded` is the explicit flag: an expired org's empty
    list is a real "no", and the bootstrap race never reaches the store.

    Failing OPEN while not loaded is the right way round: the gate is UX, and
    the server enforces regardless.
  */
  const entitlementsUsable = loaded && !!access;
  /* "Renew", not "upgrade": read-only (or the legacy expired), or an unknown
     state flagged needs_renewal. Never grace - a grace org has its plan. */
  const isExpired = isRenewalLocked(access);
  const isReadOnly = enforced && accessIsReadOnly(access);
  const inGrace = enforced && isInGrace(access);
  const trialing = enforced && isTrialing(access);

  /*
    RENEWAL IS ITS OWN MODAL. A lock met because the licence ended is not
    "your plan does not include X". Read-only says what still works (viewing,
    exporting); an admin renews, a member is told to ask one.
  */
  const showRenewalModal = useCallback(
    (label: string, readOnly: boolean = false) => {
      const title = readOnly ? translate("Read-Only Mode") : translate("License Expired");
      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: title,
          subMsg: renewalDescription({ isOrgAdmin, feature: translate(label), readOnly }),
          title,
          category: RENEWAL_CATEGORY,
          onConfirm: () => {
            history.push(UPGRADE_PATH);
          },
        })
      );
    },
    [dispatch, history, isOrgAdmin]
  );

  /** Check if the org has access to a given section */
  const hasSectionAccess = useCallback(
    (section: string): boolean => {
      if (!enforced) return true; // Pilot mode / not loaded - all access granted
      if (!entitlementsUsable) return true; // Not loaded, or not believable
      return licensedSections.includes(section);
    },
    [enforced, entitlementsUsable, licensedSections]
  );

  /** Check if the org has access to a given AI feature */
  const hasAiFeatureAccess = useCallback(
    (feature: string): boolean => {
      if (!enforced) return true;
      /* Sections, not AI features, decide believability: a real plan can hold
         zero AI features (Starter does), so an empty AI list is a fact. An
         empty SECTION list is not. */
      if (!entitlementsUsable) return true;
      return licensedAiFeatures.includes(feature);
    },
    [enforced, entitlementsUsable, licensedAiFeatures]
  );

  /**
   * Matter AI - analyse, regenerate, generate-more, contract extraction.
   *
   * `matter_ai` is the backend's key, but no seeded row grants it yet: the
   * backend treats a base plan holding any Full AI key as holding it, and until
   * that rollout reaches every environment `insights` implies it here too.
   * Starter (no AI features) answers no on both.
   */
  const hasMatterAiAccess = useCallback(
    (): boolean => hasAiFeatureAccess('matter_ai') || hasAiFeatureAccess('insights'),
    [hasAiFeatureAccess]
  );

  /** Resolve route path to license section name */
  const sectionForRoute = useCallback((route: string): string | null => {
    // Try exact match first
    if (ROUTE_TO_SECTION[route]) return ROUTE_TO_SECTION[route];
    // Try prefix match (e.g. /matters/123 → matters)
    const basePath = "/" + route.split("/").filter(Boolean)[0];
    return ROUTE_TO_SECTION[basePath] ?? null;
  }, []);

  /**
   * The section gate. `forWrite` is true for a create/change action, which a
   * read-only org may not take even in a section it holds; false for
   * navigation (`checkRouteGate`), which read-only must keep open.
   */
  const gateSection = useCallback(
    (section: string, forWrite: boolean): boolean => {
      if (!enforced) return true; // Pilot mode / not loaded - all access granted
      if (!entitlementsUsable) return true;

      const label = sectionLabel(section);
      /* READ-ONLY: the create buttons that call this would only meet a
         LICENSE_READ_ONLY 403 (or the request guard). Say it up front. */
      if (forWrite && isReadOnly) {
        showRenewalModal(label, true);
        return false;
      }
      if (licensedSections.includes(section)) return true;

      if (isExpired) {
        showRenewalModal(label);
        return false;
      }

      const { plan, isAddon } = findUnlockingPlan(section, null, plans, addons);

      // Tier-aware messaging
      let subMsg: string;
      if (isTrail) {
        // Trail users: tell them to upgrade to Starter or Full
        subMsg = `Your trial does not include <strong>${label}</strong>. Upgrade to <strong>PONS Starter</strong> or <strong>PONS Full</strong> to unlock this feature.`;
      } else if (isAddonEligible && isAddon && plan) {
        // Starter users: suggest the specific add-on
        subMsg = `Your current plan does not include <strong>${label}</strong>. Add the <strong>${plan.display_name}</strong> add-on to unlock this feature, or request it from your admin.`;
      } else if (plan) {
        subMsg = `Your current plan does not include <strong>${label}</strong>. Upgrade to <strong>${plan.display_name}</strong> to unlock this feature.`;
      } else {
        subMsg = `Your current plan does not include access to <strong>${label}</strong>. Please upgrade your plan.`;
      }

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: "Upgrade Required",
          subMsg,
          title: label,
          content: plan ? plan.display_name : undefined,
          inputData: section,
          category: isAddon ? "addon" : "base",
          onConfirm: () => {
            history.push(UPGRADE_PATH);
          },
        })
      );
      return false;
    },
    [enforced, entitlementsUsable, licensedSections, plans, addons, dispatch, history, isTrail, isAddonEligible, isExpired, isReadOnly, showRenewalModal]
  );

  /**
   * Gate a create action by section name.
   * Returns `true` if the action is allowed, `false` if blocked (modal shown).
   * Read-only orgs are blocked here even in a licensed section.
   */
  const checkSectionGate = useCallback(
    (section: string): boolean => gateSection(section, true),
    [gateSection]
  );

  /**
   * Gate a create action by AI feature name.
   * Returns `true` if the action is allowed, `false` if blocked (modal shown).
   */
  const checkAiFeatureGate = useCallback(
    (feature: string): boolean => {
      if (!enforced) return true; // Pilot mode / not loaded - all access granted
      if (!entitlementsUsable) return true;

      const featureLabel = aiFeatureLabel(feature);
      /* AI runs are POSTs: read-only refuses them like any other write. */
      if (isReadOnly) {
        showRenewalModal(featureLabel, true);
        return false;
      }
      if (licensedAiFeatures.includes(feature)) return true;

      if (isExpired) {
        showRenewalModal(featureLabel);
        return false;
      }

      const { plan, isAddon } = findUnlockingPlan(null, feature, plans, addons);

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: "Upgrade Required",
          subMsg: `Your current plan does not include <strong>${featureLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this capability.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this capability.`
              : "Please upgrade your plan to access this capability."
          }`,
          title: featureLabel,
          content: plan ? plan.display_name : undefined,
          inputData: feature,
          category: isAddon ? "addon" : "base",
          onConfirm: () => {
            history.push(UPGRADE_PATH);
          },
        })
      );
      return false;
    },
    [enforced, entitlementsUsable, licensedAiFeatures, plans, addons, dispatch, history, isExpired, isReadOnly, showRenewalModal]
  );

  /**
   * Gate NAVIGATION to a route path (the sidebar uses it).
   * Resolves route → section → gate. Read-only does not block it: viewing is
   * exactly what a read-only org may still do.
   */
  const checkRouteGate = useCallback(
    (route: string): boolean => {
      const section = sectionForRoute(route);
      if (!section) return true; // Unknown section - allow
      return gateSection(section, false);
    },
    [sectionForRoute, gateSection]
  );

  /**
   * Check if a Trail usage limit would be exceeded.
   * Pass the current count and the limit key (e.g. "max_matters").
   * Returns true if allowed, false if at/over limit (modal shown).
   */
  const checkTrailLimit = useCallback(
    (limitKey: string, currentCount: number, resourceLabel?: string): boolean => {
      if (!enforced) return true;
      if (!isTrail) return true; // Only Trail plans have hard limits

      const limitValue = limits[limitKey];
      if (limitValue === null || limitValue === undefined) return true; // No limit

      if (currentCount >= limitValue) {
        const label = resourceLabel || limitKey.replace("max_", "").replace("_", " ");
        dispatch(
          showModal({
            type: "license-request",
            showModal: true,
            message: "Trial Limit Reached",
            subMsg: `You've reached your trial limit for <strong>${label}</strong> (${currentCount}/${limitValue}). Upgrade to <strong>PONS Starter</strong> or <strong>PONS Full</strong> to continue.`,
            title: `${label} Limit`,
            category: "base",
            onConfirm: () => {
              history.push(UPGRADE_PATH);
            },
          })
        );
        return false;
      }
      return true;
    },
    [enforced, isTrail, limits, dispatch, history]
  );

  return {
    loaded,
    enforced,
    isExpired,
    isReadOnly,
    isInGrace: inGrace,
    isTrialing: trialing,
    hasBaseSeat,
    hasSectionAccess,
    hasAiFeatureAccess,
    hasMatterAiAccess,
    checkSectionGate,
    checkAiFeatureGate,
    checkRouteGate,
    checkTrailLimit,
    sectionForRoute,
    licensedSections,
    licensedAiFeatures,
    isTrail,
    isAddonEligible,
    planType,
    planName,
    limits,
  };
}
